/**
 * API client for the Agent Studio pages (Workspace, Agent Builder, shared canvas).
 *
 *   /api/v1/studio/*  — agents, files, conversations, canvases (Agent Studio engine)
 *   /api/v1/chat/*    — the Study Assistant engine (job Q&A, /run-* job creation)
 *
 * Uses fetch (not axiosInstance) because replies stream as server-sent events.
 */

// ── Shared ───────────────────────────────────────────────────────────────────

const STUDIO = "/api/v1/studio";
const CHAT = "/api/v1/chat";

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

const tenantName = () => {
  const host = window.location.hostname.split(".")[0];
  return host === "localhost" || host === "app" ? "stage" : host || "stage";
};

function authHeaders(json = true): Record<string, string> {
  const headers: Record<string, string> = { "x-tenant-name": tenantName() };
  const token = localStorage.getItem("authToken");
  if (token) headers.Authorization = `Bearer ${token}`;
  if (json) headers["Content-Type"] = "application/json";
  return headers;
}

async function errorFrom(res: Response): Promise<ApiError> {
  if (res.status === 401) window.location.href = "/#/auth/login?logout=true";
  const body = await res.json().catch(() => ({}));
  const detail = Array.isArray(body.detail)
    ? body.detail.map((d: { msg: string }) => d.msg).join("; ")
    : body.detail ?? body.error;
  return new ApiError(res.status, detail ?? res.statusText);
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, { credentials: "include", ...init, headers: { ...authHeaders(), ...init?.headers } });
  if (!res.ok) throw await errorFrom(res);
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

/** Reads an SSE response body, calling onEvent(eventName, data) for each event. */
async function readSse(res: Response, onEvent: (event: string, data: any) => void) {
  const reader = res.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let boundary = buffer.indexOf("\n\n");
    while (boundary !== -1) {
      const raw = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      let event = "message";
      let data = "";
      for (const line of raw.split("\n")) {
        if (line.startsWith("event: ")) event = line.slice(7);
        else if (line.startsWith("data: ")) data += line.slice(6);
      }
      if (data) {
        try {
          onEvent(event, JSON.parse(data));
        } catch {
          /* skip malformed event */
        }
      }
      boundary = buffer.indexOf("\n\n");
    }
  }
}

// ── Agents ───────────────────────────────────────────────────────────────────

export type ParamValue = string | number | boolean | null | ParamValue[] | { [key: string]: ParamValue };

export interface Agent {
  id: string;
  name: string;
  icon: string;
  description: string;
  instructions: string;
  tools: string[];
  defaultParams: Record<string, ParamValue>;
  isBuiltin: boolean;
  templateKey: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AgentInput {
  name: string;
  icon: string;
  description: string;
  instructions: string;
  defaultParams: Record<string, ParamValue>;
}

export const listAgents = () => api<Agent[]>(`${STUDIO}/agents`);
export const createAgent = (data: AgentInput) =>
  api<Agent>(`${STUDIO}/agents`, { method: "POST", body: JSON.stringify(data) });
export const updateAgent = (id: string, data: AgentInput) =>
  api<Agent>(`${STUDIO}/agents/${id}`, { method: "PUT", body: JSON.stringify(data) });
export const deleteAgent = (id: string) => api<void>(`${STUDIO}/agents/${id}`, { method: "DELETE" });
export const duplicateAgent = (id: string) => api<Agent>(`${STUDIO}/agents/${id}/duplicate`, { method: "POST" });

// ── Models ───────────────────────────────────────────────────────────────────

export interface ChatModel {
  id: string;
  label: string;
  provider: string;
  available: boolean;
}

/** Lays out a finished Study Assistant answer as a canvas; null when it has no chartable data. */
export const autoCanvas = (question: string, answer: string, model?: string) =>
  api<{ canvas: CanvasData | null }>(`${STUDIO}/canvas`, { method: "POST", body: JSON.stringify({ question, answer, model }) });
export const getModels = () =>
  api<{ defaultModel: string; models: ChatModel[]; assistantDefault: ChatModel }>(`${STUDIO}/models`);

// ── Workspace (Agent Studio engine) ──────────────────────────────────────────

export interface FileInfo {
  id: string;
  filename: string;
  kind: "table" | "document";
  sizeBytes: number;
  summary: { rows?: number; columns?: { name: string; dtype: string }[]; chars?: number };
  createdAt: string;
}

export type ChartBlock = {
  type: "chart";
  chart_type: "line" | "bar" | "stacked_bar" | "area" | "pie" | "scatter";
  title: string;
  x: (string | number)[];
  series: { name: string; values: (number | null)[] }[];
  x_label?: string;
  y_label?: string;
};

export type CanvasBlock =
  | { type: "heading"; text: string }
  | { type: "markdown"; text: string }
  | { type: "metrics"; items: { label: string; value: string; delta?: string; trend?: "up" | "down" | "flat" }[] }
  | { type: "table"; title?: string; columns: string[]; rows: unknown[][] }
  | ChartBlock
  | { type: "callout"; tone?: "info" | "success" | "warning"; text: string };

export interface CanvasData {
  title: string;
  blocks: CanvasBlock[];
}

export interface Step {
  id: string;
  label: string;
  status: "running" | "done" | "error";
  detail?: string | null;
}

export interface StudioMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  fileIds: string[];
  canvas: CanvasData | null;
  steps: Step[];
  /** Study setup card from Adrsta's guided setup (built-in agents), e.g. column_mapping, job_created. */
  artifact: ({ type: string } & Record<string, any>) | null;
  model: string | null;
  shareToken: string | null;
  createdAt: string;
}

export interface Conversation {
  id: string;
  agentId: string | null;
  title: string;
  model: string;
  createdAt: string;
  updatedAt: string;
}

export interface ConversationDetail extends Conversation {
  messages: StudioMessage[];
  files: FileInfo[];
}

export interface SharedCanvas {
  title: string;
  canvas: CanvasData;
  agentName: string | null;
  createdAt: string;
}

export const listConversations = () => api<Conversation[]>(`${STUDIO}/conversations`);
export const getConversation = (id: string) => api<ConversationDetail>(`${STUDIO}/conversations/${id}`);
export const createConversation = (data: { agentId?: string | null; model?: string }) =>
  api<Conversation>(`${STUDIO}/conversations`, { method: "POST", body: JSON.stringify(data) });
export const updateConversation = (id: string, data: { agentId?: string | null; model?: string; title?: string }) =>
  api<Conversation>(`${STUDIO}/conversations/${id}`, { method: "PATCH", body: JSON.stringify(data) });
export const deleteConversation = (id: string) => api<void>(`${STUDIO}/conversations/${id}`, { method: "DELETE" });
export const shareCanvas = (messageId: string) =>
  api<{ token: string }>(`${STUDIO}/messages/${messageId}/share`, { method: "POST" });
export const unshareCanvas = (messageId: string) =>
  api<void>(`${STUDIO}/messages/${messageId}/share`, { method: "DELETE" });

/** Public (no login) — used by the shared canvas page. */
export async function getSharedCanvas(token: string): Promise<SharedCanvas> {
  const res = await fetch(`${STUDIO}/share/${encodeURIComponent(token)}`, { headers: { "x-tenant-name": tenantName() } });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, body.detail ?? "This canvas could not be loaded.");
  return body as SharedCanvas;
}

export async function uploadFile(file: File): Promise<FileInfo> {
  const body = new FormData();
  body.append("file", file);
  const res = await fetch(`${STUDIO}/files`, { method: "POST", body, credentials: "include", headers: authHeaders(false) });
  if (!res.ok) throw await errorFrom(res);
  return res.json();
}

export type StudioEvent =
  | { event: "user_message"; data: StudioMessage }
  | { event: "token"; data: { text: string } }
  | { event: "step"; data: Step }
  | { event: "canvas"; data: CanvasData }
  | { event: "artifact"; data: { type: string } & Record<string, any> }
  | { event: "done"; data: StudioMessage }
  | { event: "error"; data: { message: string } };

/** Sends a message and calls onEvent for each server-sent event until the turn ends. */
export async function sendStudioMessage(
  conversationId: string,
  body: { content: string; fileIds: string[]; model: string },
  onEvent: (e: StudioEvent) => void,
  signal: AbortSignal,
): Promise<void> {
  const res = await fetch(`${STUDIO}/conversations/${conversationId}/messages`, {
    method: "POST",
    credentials: "include",
    headers: authHeaders(),
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok || !res.body) throw await errorFrom(res);
  await readSse(res, (event, data) => onEvent({ event, data } as StudioEvent));
}

// ── Study Assistant engine (/api/v1/chat) ────────────────────────────────────

export interface AssistantSession {
  session_id: string;
  name: string;
  session_type: string;
  last_message_at: string;
  last_message_preview: string | null;
  message_count: number;
}

export interface AssistantColumn {
  name: string;
  detected_role: string;
  sample_values: string[];
  confidence: number;
  display_label: string;
}

export interface AssistantAttachment {
  s3_key: string;
  filename: string;
  row_count: number;
  columns: AssistantColumn[];
  preview_rows: any[];
}

export const listAssistantSessions = async (): Promise<AssistantSession[]> =>
  (await api<{ sessions?: AssistantSession[] }>(`${CHAT}/sessions?limit=50`)).sessions ?? [];

export const getAssistantHistory = async (sessionId: string) =>
  (await api<{ messages?: { role: string; content: string; timestamp?: string }[] }>(`${CHAT}/history/${sessionId}`))
    .messages ?? [];

export const deleteAssistantSession = (sessionId: string) =>
  api<unknown>(`${CHAT}/sessions/${sessionId}`, { method: "DELETE" });

export async function uploadAssistantCsv(file: File): Promise<AssistantAttachment> {
  const body = new FormData();
  body.append("file", file);
  const res = await fetch(`${CHAT}/upload`, { method: "POST", body, credentials: "include", headers: authHeaders(false) });
  if (!res.ok) throw await errorFrom(res);
  return res.json();
}

/** Legacy stream: each SSE `data:` line is {type, content?, payload?, session_id?, ...}. */
export async function sendAssistantMessage(
  body: { query: string; session_id?: string; attachment?: AssistantAttachment; model?: string },
  onEvent: (e: any) => void,
  signal: AbortSignal,
): Promise<void> {
  const res = await fetch(`${CHAT}/stream`, {
    method: "POST",
    credentials: "include",
    headers: authHeaders(),
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok || !res.body) throw await errorFrom(res);
  await readSse(res, (_event, data) => onEvent(data));
}
